import { lazy } from "react";
import { loadRemote, registerRemotes, } from "@module-federation/enhanced/runtime";
import { IS_SERVER_ENVIRONMENT } from "@aviv-unified-frontend/internal";
import { ALLOW_RUNTIME_FEDERATION_VERSION_OVERLOADING } from "../../constants/environment";
import { getRemoteApplication } from "../build/getBuildApplication";
import { registry } from "../../../configs/registry";
import { fetchRemoteComponent } from "../utils/fetchRemoteComponent";
import { updateUrlVersion } from "../utils/updateUrlVersion";
import { APP_VERION_KEY } from "./constants";
import { APPLICATIONS_CACHED } from "./createApplicationCache";
import { createRuntimeWrapper } from "./createRuntimeWrapper";
export const loadRuntimeApplication = async (application, request) => {
    const { id, version } = application;
    let url = application.urls[IS_SERVER_ENVIRONMENT ? "server" : "client"];
    if (!url) {
        throw new Error(`loadRuntimeApplication: no URL found for application: ${id}`);
    }
    const applicationCached = APPLICATIONS_CACHED.getApplication(id);
    const runtimeVersion = ALLOW_RUNTIME_FEDERATION_VERSION_OVERLOADING
        ? getRuntimeApplicationVersionQueryParameter(request)
        : undefined;
    if (runtimeVersion) {
        url = updateUrlVersion(url, runtimeVersion);
    }
    let ETag = null;
    let shouldRevalidate = false;
    if (IS_SERVER_ENVIRONMENT) {
        ETag = await getRemoteEntryETag(url);
        shouldRevalidate = Boolean(applicationCached && applicationCached.ETag !== ETag);
    }
    if (applicationCached && !shouldRevalidate) {
        return applicationCached;
    }
    /**
     * registerRemotes
     * https://module-federation.io/guide/basic/runtime.html#registerremotes
     */
    registerRemotes([
        {
            name: id,
            entry: url,
        },
    ], {
        force: IS_SERVER_ENVIRONMENT,
    });
    const remoteApplication = getRemoteApplication({
        id,
        exports: (await loadRemote(id)),
    });
    const Application = {
        ...remoteApplication,
        ...(ALLOW_RUNTIME_FEDERATION_VERSION_OVERLOADING
            ? { Component: createRuntimeWrapper(id, remoteApplication.Component) }
            : {}),
        version: runtimeVersion ?? version.toString(),
        ETag,
    };
    APPLICATIONS_CACHED.addApplication(id, Application);
    return Application;
};
const getRemoteEntryETag = async (url) => {
    const { headers } = await fetchRemoteComponent(url, {
        method: "HEAD",
    });
    return headers.get("etag");
};
export const loadRuntimeApplicationById = (id) => {
    const application = registry.getApplicationFromId(id);
    if (!application) {
        throw new Error(`loadRuntimeApplicationById: application not found: ${id}`);
    }
    return loadRuntimeApplication(application);
};
/**
 * Lazy load a remote component.
 * For React best practices, follow {@link https://react.dev/reference/react/lazy#troubleshooting | this documentation}.
 */
export const getLazyRuntimeApplicationComponent = (application) => {
    if (!application) {
        throw new Error(`Unable to lazy load the remote component. Make sure the registry is configured correctly to import an existing component.`);
    }
    return lazy(async () => ({
        default: (await loadRuntimeApplication(application)).Component,
    }));
};
export const setRuntimeApplicationVersionQueryParameter = (version) => {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set(APP_VERION_KEY, version);
    window.location.href = currentUrl.toString();
};
export const getRuntimeApplicationVersionQueryParameter = (request) => {
    let value;
    if (IS_SERVER_ENVIRONMENT) {
        value = request?.query[APP_VERION_KEY];
    }
    else {
        const urlParams = new URLSearchParams(window.location.search);
        value = urlParams.get(APP_VERION_KEY);
    }
    return typeof value === "string" ? value : undefined;
};
